import { Injectable, signal } from '@angular/core';

export interface HubToast {
  id: number;
  kind: 'success' | 'error';
  message: string;
}

/** Minimal toast queue for the merchant hub. */
@Injectable({ providedIn: 'root' })
export class HubToastService {
  private seq = 0;
  readonly toasts = signal<HubToast[]>([]);

  success(message: string): void {
    this.push('success', message);
  }

  error(message: string): void {
    this.push('error', message);
  }

  dismiss(id: number): void {
    this.toasts.update((list) => list.filter((toast) => toast.id !== id));
  }

  private push(kind: HubToast['kind'], message: string): void {
    const id = ++this.seq;
    this.toasts.update((list) => [...list, { id, kind, message }]);
    setTimeout(() => this.dismiss(id), kind === 'error' ? 8000 : 4000);
  }
}

/** Extract a back-office safe message from an API error response. */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  const body = (error as { error?: unknown } | null)?.error;
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    if (typeof record['detail'] === 'string') {
      return record['detail'];
    }
    if (typeof record['error'] === 'string') {
      return record['error'];
    }
    const first = Object.entries(record).find(([, value]) => value !== undefined);
    if (first) {
      const [field, value] = first;
      const text = Array.isArray(value) ? value.join(' ') : String(value);
      return field === 'non_field_errors' ? text : `${field}: ${text}`;
    }
  }
  return fallback;
}
